import { FC, Dispatch, SetStateAction } from 'react';

import {
  Header,
  Button,
  SpaceBetween,
  Table,
  Flashbar,
  Box,
  TextFilter,
  Pagination,
  ButtonDropdown
} from '@awsui/components-react';
import { useCollection } from '@awsui/collection-hooks';

import { FlashbarProps } from '@awsui/components-react';
import { IUserWorkSpaceDecommissionRequest } from '../../../../../interfaces/workSpaces/userWorkSpaceDecommissionRequest';

import RejectUserWorkSpaceDecommissionRequestForm from '../rejectUserWorkSpaceDecommissionRequestForm';
import UserWorkSpaceDecommissionRequestDetails from '../userWorkSpaceDecommissionRequestDetails';
import ChildModal from '../../../../common/ChildModal';

interface ViewProps {
  userWorkSpaceDecommissionRequests: IUserWorkSpaceDecommissionRequest[];
  selectedUserWorkSpaceDecommissionRequests: IUserWorkSpaceDecommissionRequest[];
  flashBarItems: FlashbarProps.MessageDefinition[];
  setFlashBarItems: Dispatch<SetStateAction<FlashbarProps.MessageDefinition[]>>;
  disableActionButtons: boolean;
  handleTableRowSelection: (user: IUserWorkSpaceDecommissionRequest[]) => void;
  tableIsLoading: boolean;
  setRejectUserWorkSpaceDecommissionRequestModalIsVisible: Dispatch<
    SetStateAction<boolean>
  >;
  rejectUserWorkSpaceDecommissionRequestModalIsVisible: boolean;
  handleApproval: () => void;
  refreshUserWorkSpaceDecommissionRequestTable: () => void;
  userWorkSpaceDecommissionRequestDetailsModalIsVisible: boolean;
  setUserWorkSpaceDecommissionRequestDetailsModalIsVisible: Dispatch<
    SetStateAction<boolean>
  >;
}

const View: FC<ViewProps> = ({
  userWorkSpaceDecommissionRequests,
  selectedUserWorkSpaceDecommissionRequests,
  flashBarItems,
  setFlashBarItems,
  handleTableRowSelection,
  disableActionButtons,
  tableIsLoading,
  setRejectUserWorkSpaceDecommissionRequestModalIsVisible,
  rejectUserWorkSpaceDecommissionRequestModalIsVisible,
  handleApproval,
  refreshUserWorkSpaceDecommissionRequestTable,
  userWorkSpaceDecommissionRequestDetailsModalIsVisible,
  setUserWorkSpaceDecommissionRequestDetailsModalIsVisible
}) => {
  const renderEmptyTableContent = () => {
    return (
      <Box textAlign="center" color="inherit">
        <b>No WorkSpace Decommission Requests</b>
        <Box padding={{ bottom: 's' }} variant="p" color="inherit">
          No WorkSpace Decommissiont Requests to display.
        </Box>
      </Box>
    );
  };
  const renderEmptyTableContentAfterFiltering = () => {
    return (
      <Box textAlign="center" color="inherit">
        <b>No matching User WorkSpace Decommission Requests</b>
      </Box>
    );
  };

  const {
    items,
    filteredItemsCount,
    collectionProps,
    filterProps,
    paginationProps
  } = useCollection(userWorkSpaceDecommissionRequests, {
    filtering: {
      empty: renderEmptyTableContent(),
      noMatch: renderEmptyTableContentAfterFiltering(),
      fields: [
        'firstName',
        'lastName',
        'corpEmail',
        'homeGroup',
        'justification'
      ]
    },
    pagination: { pageSize: 10 },
    sorting: {},
    selection: {}
  });
  const { selectedItems: filteredUserWorkSpaceDecommissionRequests = [] } =
    collectionProps;

  const renderTableActions = () => {
    return (
      <>
        <SpaceBetween direction="horizontal" size="xs">
          <Button
            iconName="refresh"
            variant="link"
            onClick={() => refreshUserWorkSpaceDecommissionRequestTable()}
          />
          <Button
            variant="primary"
            disabled={disableActionButtons}
            onClick={() =>
              setUserWorkSpaceDecommissionRequestDetailsModalIsVisible(true)
            }
          >
            View
          </Button>

          <ButtonDropdown
            disabled={disableActionButtons}
            items={[
              {
                text: 'Approve',
                id: 'approve-request',
                disabled: disableActionButtons
              },
              {
                text: 'Reject',
                id: 'reject-request',
                disabled: disableActionButtons
              }
            ]}
            onItemClick={({ detail }) => {
              if (detail.id === 'approve-request') {
                handleApproval();
              } else if (detail.id === 'reject-request') {
                setRejectUserWorkSpaceDecommissionRequestModalIsVisible(true);
              }
            }}
          >
            Actions
          </ButtonDropdown>
        </SpaceBetween>
      </>
    );
  };
  const renderTableHeader = () => {
    return (
      <Header
        counter={
          filteredUserWorkSpaceDecommissionRequests.length
            ? `(${filteredUserWorkSpaceDecommissionRequests.length}/${userWorkSpaceDecommissionRequests.length})`
            : `(${userWorkSpaceDecommissionRequests.length})`
        }
        actions={renderTableActions()}
      >
        Pending WorkSpace Decommission Requests
      </Header>
    );
  };

  const renderRejectUserWorkSpaceDecommissionRequestModal = () => {
    return (
      <ChildModal
        onDismiss={() => {
          setRejectUserWorkSpaceDecommissionRequestModalIsVisible(false);
        }}
        visible={rejectUserWorkSpaceDecommissionRequestModalIsVisible}
        closeAriaLabel="Close modal"
        size="medium"
        header="Reject WorkSpace Decommission Request"
      >
        <RejectUserWorkSpaceDecommissionRequestForm
          userWorkSpaceDecommissionRequests={
            selectedUserWorkSpaceDecommissionRequests
          }
          setFlashBarItems={setFlashBarItems}
          setRejectUserWorkSpaceDecommissionRequestModalIsVisible={
            setRejectUserWorkSpaceDecommissionRequestModalIsVisible
          }
          refreshUserWorkSpaceDecommissionRequestTable={
            refreshUserWorkSpaceDecommissionRequestTable
          }
        />
      </ChildModal>
    );
  };

  const renderUserWorkSpaceDecommissionRequestDetailsModal = () => {
    return (
      <ChildModal
        onDismiss={() => {
          setUserWorkSpaceDecommissionRequestDetailsModalIsVisible(false);
        }}
        visible={userWorkSpaceDecommissionRequestDetailsModalIsVisible}
        closeAriaLabel="Close modal"
        size="large"
        header="WorkSpace Decommission Request Details"
      >
        <UserWorkSpaceDecommissionRequestDetails
          selectedUserWorkSpaceDecommissionRequests={
            selectedUserWorkSpaceDecommissionRequests
          }
        />
      </ChildModal>
    );
  };

  const tableColumnDefinitions = [
    {
      id: 'userName',
      header: 'Username',
      cell: (item: IUserWorkSpaceDecommissionRequest) => item.userName || '-'
    },
    {
      id: 'corpEmail',
      header: 'Corp Email',
      cell: (item: IUserWorkSpaceDecommissionRequest) => item.corpEmail || '-'
    },
    {
      id: 'workSpaceId',
      header: 'WorkSpace ID',
      cell: (item: IUserWorkSpaceDecommissionRequest) => item.workSpaceId || '-'
    },
    {
      id: 'justification',
      header: 'Justification',
      cell: (item: IUserWorkSpaceDecommissionRequest) =>
        item.justification || '-'
    }
  ];

  return (
    <>
      <SpaceBetween direction="vertical" size="xs">
        <Flashbar items={flashBarItems} />
        <Table
          {...collectionProps}
          loading={tableIsLoading}
          trackBy="uuid"
          selectedItems={selectedUserWorkSpaceDecommissionRequests}
          selectionType="single"
          columnDefinitions={tableColumnDefinitions}
          items={items}
          loadingText="Loading Users"
          onSelectionChange={({ detail }) =>
            handleTableRowSelection(detail.selectedItems)
          }
          header={renderTableHeader()}
          filter={
            <TextFilter
              {...filterProps}
              countText={`${filteredItemsCount} matches`}
              filteringAriaLabel="Filter Users"
            />
          }
          pagination={
            <Pagination
              {...paginationProps}
              ariaLabels={{
                nextPageLabel: 'Next page',
                previousPageLabel: 'Previous page',
                pageLabel: (pageNumber) => `Page ${pageNumber} of all pages`
              }}
            />
          }
        />
      </SpaceBetween>
      {renderRejectUserWorkSpaceDecommissionRequestModal()}
      {renderUserWorkSpaceDecommissionRequestDetailsModal()}
    </>
  );
};
export default View;
